<template>
  <span>
    <v-btn
      v-if="!isPrinting"
      class="caption font-weight-bold"
      :large="!small"
      :small="small"
      elevation="2"
      rounded
      color="yellow"
      @click="printNotes()"
    >
      <v-icon v-if="showPencilIcon" left color="green darken-2">mdi-pencil</v-icon>
      {{ buttonText }}
      <v-icon :small="small" right>
        mdi-tray-arrow-down
      </v-icon>
    </v-btn>
    <v-btn
      v-else
      class="caption font-weight-bold"
      :large="!small"
      :small="small"
      elevation="2"
      rounded
      color="yellow"
    >
      <v-progress-circular indeterminate color="primary" :size="small ? 16 : 24"></v-progress-circular>
    </v-btn>
    <vue-html2pdf
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="800px"
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="false"
      :paginate-elements-by-height="1100"
      :pdf-quality="1"
      :manual-pagination="false"
      :pdf-margin="40"
      :html-to-pdf-options="{
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], before: '.page-break-before', after: '.page-break-after', avoid: '.avoid-break' },
        filename: getFileName(),
        margin: [20, 0, 10, 0],
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }"
      @beforeDownload="beforeDownload($event)"
      @progress="onProgress($event)"
      @hasStartedGeneration="hasStartedGeneration()"
      @hasGenerated="hasGenerated($event)"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <section class="pdf-item" style="margin-left:15%;margin-right:15%">
          <!-- Logo -->
          <div style="text-align:center;margin-bottom:30px;">
            <img :src="configLogoUrl" style="max-width:200px;min-width:100px;max-height:200px;" />
          </div>
          
          <h2 style="margin-bottom:0;">Build Your Own Case Study</h2>
          <h3 style="margin-top:0;margin-bottom:10px;">Notes Transcript</h3>
          <h3 v-if="caseDescription" style="margin-top:0;margin-bottom:40px;color:#555;">{{ caseDescription }}</h3>
          
          <div style="padding-bottom:50px;">
            <span v-if="user.fName !== 'FirstNameNotSet'">
              {{ user.fName }} {{ user.lName }},
              <br /><br />
            </span>
            Thank you for completing this <strong>Build Your Own Case Study</strong> course.
            <br /><br />
            Below are the notes that you entered during your time in the course.
          </div>

          <!-- Dynamic notes rendering - works with new format -->
          <div v-for="(stepData, stepName) in notesToPrint" :key="stepName" class="avoid-break" style="padding-bottom:40px; page-break-inside: avoid;">
            <h5 style="margin-bottom:5px; page-break-after: avoid;">******** {{ stepName.toUpperCase() }} NOTES ********</h5>
            
            <!-- Show avatar name if available (new format) -->
            <p v-if="stepData._avatar" style="margin-top:0; margin-bottom:15px; font-style:italic; color:#666;">
              Patient: {{ stepData._avatar }}
            </p>
            
            <!-- Check if stepData is an array (legacy flat notes) or object (new sectioned notes) -->
            <template v-if="Array.isArray(stepData)">
              <ul style="page-break-inside: avoid;">
                <li v-for="(note, noteIndex) in stepData" :key="noteIndex" class="avoid-break" style="padding-bottom:10px; page-break-inside: avoid;">
                  {{ note }}
                </li>
              </ul>
            </template>
            
            <!-- Sectioned notes (new format with _sections) -->
            <template v-else-if="stepData._sections">
              <div v-for="(sectionNotes, sectionName) in stepData._sections" :key="sectionName" class="avoid-break" style="margin-bottom:20px; page-break-inside: avoid;">
                <h6 style="margin-bottom:10px; color:#555; page-break-after: avoid;">{{ sectionName }}</h6>
                <ul style="page-break-inside: avoid;">
                  <li v-for="(note, noteIndex) in sectionNotes" :key="noteIndex" class="avoid-break" style="padding-bottom:8px; page-break-inside: avoid;">
                    {{ note }}
                  </li>
                </ul>
              </div>
            </template>
          </div>

        </section>
      </section>
    </vue-html2pdf>
  </span>
</template>

<script>
import axios from "axios";
import VueHtml2pdf from "vue-html2pdf";
import { mapState, mapActions } from "vuex";
import caseConfigMixin from "/src/mixins/caseConfigMixin.js";
export default {
  name: "PrintNotes",
  mixins: [caseConfigMixin],
  components: {
    VueHtml2pdf,
  },
  props: {
    user: {
      type: Object,
      required: true,
      default: {},
    },
    small: {
      type: Boolean,
      default: false,
    },
    showPencilIcon: {
      type: Boolean,
      default: true,
    },
    buttonText: {
      type: String,
      default: 'Print My Notes',
    },
  },
  data() {
    return {
      notes: [],
      notesToPrint: [],
      isPrinting: false,
    };
  },
  computed: {
    ...mapState({
      caseDescription: (state) => state.case.description,
    }),
  },
  watch: {
    visible(newValue) {
      if (!newValue) {
        this.clearCurrentNote();
      }
    },
  },
  async beforeMount() {
    await this.notesForPrint();
  },
  methods: {
    ...mapActions(["trackingAction"]),
    getFileName() {
      let current = new Date().toLocaleString();
      let filename = 'BYOCS Notes - ' + this.user.fName + this.user.lName + ' ' + current + '.pdf';
      if (this.user.fName === "FirstNameNotSet" || this.user.lName === "LastNameNotSet" ) {
        filename = 'BYOCS Notes - ' + ' ' + current + '.pdf';
      } else {
        filename = 'BYOCS Notes - ' + this.user.fName + this.user.lName + ' ' + current + '.pdf';
      }

      return filename;
    },
    hasStartedGeneration() {
      console.log("$hasStartedGeneration");
    },
    hasGenerated($event) {
      console.log("$hasGenerated", $event);
    },
    async onProgress($event) {
      console.log("$onProgress", $event);
    },
    async beforeDownload({ html2pdf, options, pdfContent }) {
      let Year = new Date().getFullYear();
      await html2pdf()
        .set(options)
        .from(pdfContent)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          if (totalPages >= 3) {
            pdf.deletePage(1);
          }
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(150);
            pdf.text(
              "©" +
                Year +
                " Medical Learning Institute Inc | All Rights Reserved | www.mlieducation.com",
              pdf.internal.pageSize.getWidth() * 0.25,
              pdf.internal.pageSize.getHeight() - 5
            );
          }
        })
        .save();
      console.log("done");
      this.isPrinting = false;
    },
    async notesForPrint() {
      let uuid = this.user.uuid;
      try {
        let response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/notesForPrint`,
          { params: { uuid } }
        );
        this.notesToPrint = response.data;
      } catch (err) {
        console.log(err);
      }
    },
    printNotes() {
      this.isPrinting = true;
      this.$refs.html2Pdf.generatePdf();
      this.trackingAction("User Notes Downloaded");
    },
  },
};
</script>

<style scoped></style>
